import type { DispatchCard, RouteAnchor } from 'src/entities/dispatch';

export type { RouteAnchor };

export type NoDeliveryPlacementView<T> = {
  /** 動かすカード（本日なし・未反映）だけが返す。ルートでその地点より前の地点を近い順に全部（無ければ []）。それ以外は undefined */
  routeAnchorsOf: (card: T) => readonly RouteAnchor[] | undefined;
  /**
   * 今日の配車のカード（実 operation の積地・降地）だけが返す。座標で畳んだカードは畳んだ案件の地点をすべて返す
   * （代表名だけだと、畳まれた側の地点を 1 つ前の地点として見つけられない。log_web_issues#403 の 3）。未反映・本日なしは []
   */
  anchorsOf: (card: T) => readonly RouteAnchor[];
  /** 行の先頭から続く積地カード群を数える */
  isLoading: (card: T) => boolean;
};

const sameAnchor = (a: RouteAnchor, b: RouteAnchor) => a.stopType === b.stopType && a.placeName === b.placeName;

/**
 * 本日なし・未反映のカードを、ダイヤのルートで 1 つ前にある地点のカードの直後へ移す（log_web_issues#401 の 1・#403 の 3）。
 * 1 つ前が今日の配車に無ければさらに前へさかのぼり、先頭まで無ければ行の先頭の積地カード群の直後。
 * 動かすカード（routeAnchorsOf が返すもの）以外の並びは変えない。名前は本日なしだけを動かしていた頃のまま。
 *
 * Why not ルートの sequence_no で全カードを並べ直す: 今日のカードの順は手で並べ替えた結果
 * （サーバの arrivalAt）で、ルートと違ってよい。動かすのは本日なし・未反映だけ。
 */
export function placeNoDeliveryCards<T>(cards: readonly T[], view: NoDeliveryPlacementView<T>): T[] {
  // 同じ地点の後ろに続けて入れるのでルートの順に処理する。前にある地点の数がルート上の位置になる
  // （D&D のあとは本日なし同士の並びがルートの順とは限らない）。Array.prototype.sort は安定
  const noDeliveries = cards
    .filter((c) => view.routeAnchorsOf(c) !== undefined)
    .sort((a, b) => (view.routeAnchorsOf(a)?.length ?? 0) - (view.routeAnchorsOf(b)?.length ?? 0));
  if (noDeliveries.length === 0) return cards as T[];

  const result = cards.filter((c) => view.routeAnchorsOf(c) === undefined);
  // 基準のカードごとに、直後へ入れた本日なしの枚数。続けて入れる本日なしをその後ろに置き、ルートの順を保つ
  const insertedAfter = new Map<T | null, number>();

  for (const nd of noDeliveries) {
    const anchors = view.routeAnchorsOf(nd) ?? [];
    let base: T | null = null;
    for (const anchor of anchors) {
      const hit = result.find((c) => view.anchorsOf(c).some((own) => sameAnchor(own, anchor)));
      if (hit !== undefined) {
        base = hit;
        break;
      }
    }

    let index: number;
    if (base !== null) {
      index = result.indexOf(base) + 1;
    } else {
      index = 0;
      while (index < result.length && view.isLoading(result[index])) index += 1;
    }
    const offset = insertedAfter.get(base) ?? 0;
    result.splice(index + offset, 0, nd);
    insertedAfter.set(base, offset + 1);
  }
  return result;
}

/**
 * 画面のカード（DispatchCard）で本日なし・未反映を置く。D&D 中の上書き（useAssignmentOverride）が使う。
 * routeAnchors を持たないカード（ダイヤだけの回転の行）は動かさない。
 * template は D&D で置いた今日の配車なので基準に数え、fr-template（未反映）は数えない（動かす側）。
 */
export const dispatchCardNoDeliveryView: NoDeliveryPlacementView<DispatchCard> = {
  routeAnchorsOf: (card) => (card.type === 'delivery' && (card.status === 'no-delivery' || card.status === 'fr-template')
    ? card.routeAnchors : undefined),
  anchorsOf: (card) => {
    if (card.type === 'loading') {
      const names = card.anchorNames ?? (card.locationName != null ? [card.locationName] : []);
      return names.map((placeName) => ({ stopType: 'loading' as const, placeName }));
    }
    if (card.status !== 'matched' && card.status !== 'template') return [];
    return (card.anchorNames ?? [card.storeName]).map((placeName) => ({ stopType: 'unloading' as const, placeName }));
  },
  isLoading: (card) => card.type === 'loading',
};
